// Dashboard Page - Main page after login
// Contains navigation tabs for all features
// UPDATED: Added company logo in TOP RIGHT corner next to user menu

import React, { useState, useRef, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import MainDashboard from '../components/MainDashboard';
import FlashReportForm from '../components/FlashReportForm';
import FlashReportView from '../components/FlashReportView';
import UtilitiesForm from '../components/UtilitiesForm';
import UtilitiesDashboard from '../components/UtilitiesDashboard';
import UtilitiesReportView from '../components/UtilitiesReportView';
import UserManagement from '../components/UserManagement';
import UtilitiesPerLbSettings from '../components/UtilitiesPerLbSettings';
import '../styles/Dashboard.css';

function Dashboard() {
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    const [activeTab, setActiveTab] = useState('main');
    const [showDataEntryDropdown, setShowDataEntryDropdown] = useState(false);
    const [showUserMenu, setShowUserMenu] = useState(false);
    const [showMobileMenu, setShowMobileMenu] = useState(false);
    const dataEntryRef = useRef(null);
    const userMenuRef = useRef(null);

    // Close dropdowns when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dataEntryRef.current && !dataEntryRef.current.contains(event.target)) {
                setShowDataEntryDropdown(false);
            }
            if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
                setShowUserMenu(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Handle logout
    const handleLogout = () => {
        logout();
        navigate('/login');
    };

    // Handle data entry selection
    const handleDataEntrySelect = (tab) => {
        setActiveTab(tab);
        setShowDataEntryDropdown(false);
        setShowMobileMenu(false);
    };

    // Handle tab selection
    const handleTabSelect = (tab) => {
        setActiveTab(tab);
        setShowMobileMenu(false);
    };

    return (
        <div className="dashboard-container">
            <header className="dashboard-header">
                <div className="header-left">
                    <button 
                        className="hamburger-button"
                        onClick={() => setShowMobileMenu(!showMobileMenu)}
                        aria-label="Toggle menu"
                    >
                        <span className={`hamburger-line ${showMobileMenu ? 'open' : ''}`}></span>
                        <span className={`hamburger-line ${showMobileMenu ? 'open' : ''}`}></span>
                        <span className={`hamburger-line ${showMobileMenu ? 'open' : ''}`}></span>
                    </button>
                    <h1>Flash | Utilities</h1>
                </div>
                <div className="header-right">
                    {/* ========================================== */}
                    {/* COMPANY LOGO - Next to user menu */}
                    {/* Shows on all pages in top-right corner */}
                    {/* ========================================== */}
                    <img 
                        src="/logos/header-logo.png" 
                        alt="Company Logo" 
                        className="header-logo"
                        onError={(e) => {
                            // If logo doesn't exist, just hide it (no fallback needed here)
                            e.target.style.display = 'none';
                        }}
                    />
                    
                    <div className="user-menu-container" ref={userMenuRef}>
                        <button 
                            className="user-menu-button"
                            onClick={() => setShowUserMenu(!showUserMenu)}
                        >
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                                <circle cx="12" cy="7" r="4"></circle>
                            </svg>
                            <span>{user?.fullName}</span>
                            <svg className={`dropdown-arrow ${showUserMenu ? 'open' : ''}`} width="12" height="12" viewBox="0 0 12 12" fill="currentColor">
                                <path d="M2 4l4 4 4-4z"/>
                            </svg>
                        </button>
                        
                        {showUserMenu && (
                            <div className="user-dropdown">
                                <div className="user-dropdown-header">
                                    <div className="user-name">{user?.fullName}</div>
                                    <div className="user-role">{user?.isAdmin ? 'Administrator' : 'User'}</div>
                                </div>
                                <div className="user-dropdown-divider"></div>
                                {user?.isAdmin && (
                                    <button 
                                        className="user-dropdown-item"
                                        onClick={() => {
                                            setActiveTab('users');
                                            setShowUserMenu(false);
                                        }}
                                    >
                                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                            <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
                                            <circle cx="9" cy="7" r="4"></circle>
                                            <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
                                            <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
                                        </svg>
                                        User Management
                                    </button>
                                )}
                                {/* Admin only: per-plant Utilities Per Pound switch */}
                                {user?.isAdmin && (
                                    <button
                                        className="user-dropdown-item"
                                        onClick={() => {
                                            setActiveTab('utilities-per-lb');
                                            setShowUserMenu(false);
                                        }}
                                    >
                                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                            <rect x="1" y="7" width="22" height="10" rx="5"></rect>
                                            <circle cx="16" cy="12" r="3"></circle>
                                        </svg>
                                        Utilities Per Pound
                                    </button>
                                )}
                                <button 
                                    className="user-dropdown-item logout"
                                    onClick={handleLogout}
                                >
                                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"></path>
                                        <polyline points="16 17 21 12 16 7"></polyline>
                                        <line x1="21" y1="12" x2="9" y2="12"></line>
                                    </svg>
                                    Logout
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            </header>

            {/* Tab Navigation */}
            <div className={`tab-navigation ${showMobileMenu ? 'mobile-open' : ''}`}>
                <button 
                    className={`tab-button ${activeTab === 'main' ? 'active' : ''}`}
                    onClick={() => handleTabSelect('main')}
                >
                    Flash Report
                </button>

                <button 
                    className={`tab-button ${activeTab === 'report' ? 'active' : ''}`}
                    onClick={() => handleTabSelect('report')}
                >
                    Flash 13 Week View
                </button>
                <button 
                    className={`tab-button ${activeTab === 'utilities-dashboard' ? 'active' : ''}`}
                    onClick={() => handleTabSelect('utilities-dashboard')}
                >
                    Utilities Report
                </button>
                <button 
                    className={`tab-button ${activeTab === 'utilities-report' ? 'active' : ''}`}
                    onClick={() => handleTabSelect('utilities-report')}
                >
                    Utilities 6 Month View
                </button>

                {/* Data Entry Dropdown */}
                <div className="dropdown-container" ref={dataEntryRef}>
                    <button 
                        className={`tab-button dropdown-trigger ${(activeTab === 'entry' || activeTab === 'utilities') ? 'active' : ''}`}
                        onClick={() => setShowDataEntryDropdown(!showDataEntryDropdown)}
                    >
                        Data Entry
                        <svg className={`dropdown-arrow ${showDataEntryDropdown ? 'open' : ''}`} width="12" height="12" viewBox="0 0 12 12" fill="currentColor">
                            <path d="M2 4l4 4 4-4z"/>
                        </svg>
                    </button>
                    
                    {showDataEntryDropdown && (
                        <div className="dropdown-menu">
                            <button 
                                className={`dropdown-item ${activeTab === 'entry' ? 'active' : ''}`}
                                onClick={() => handleDataEntrySelect('entry')}
                            >
                                Flash Data Entry
                            </button>
                            <button 
                                className={`dropdown-item ${activeTab === 'utilities' ? 'active' : ''}`}
                                onClick={() => handleDataEntrySelect('utilities')}
                            >
                                Utilities Data Entry
                            </button>
                        </div>
                    )}
                </div>

            </div>

            <div className="dashboard-content">
                {activeTab === 'main' && <MainDashboard />}
                {activeTab === 'entry' && <FlashReportForm />}
                {activeTab === 'report' && <FlashReportView />}
                {activeTab === 'utilities' && <UtilitiesForm />}
                {activeTab === 'utilities-dashboard' && <UtilitiesDashboard />}
                {activeTab === 'utilities-report' && <UtilitiesReportView />}
                {activeTab === 'users' && user?.isAdmin && <UserManagement />}
                {activeTab === 'utilities-per-lb' && user?.isAdmin && <UtilitiesPerLbSettings />}
            </div>
        </div>
    );
}

export default Dashboard;
